import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns-tz'

import { formatPrice } from '@/utils'

import Loader from '../../Loader/Loader'
import { dayMinutesToHoursMinutes } from '../../../helpers/helpers'
import { isDisabledAmongRange } from '../../ClientReservations/Amenities/helpers'

import { getHourlySlotsForTime } from './helper'

const ReservationTimeSelect = ({
  amenity,
  selectedDate,
  onValueChange,
  onGoBack,
  value,
  timeSlots = [],
  loadingTimeScopes = false,
  locationId,
}) => {
  const { t } = useTranslation(['reservations'])
  const { minTime, maxTime, timeInterval } = amenity
  const isRangeAmenity = timeInterval < maxTime

  const capacityDisabledSlots = useMemo(
    () =>
      timeSlots
        .filter(({ reservations, locations }) => {
          if (locationId) {
            const locationData = locations.find((loc) => loc.id === locationId)
            return !locationData || locationData.capacity <= 0
          }
          return reservations >= amenity.capacity
        })
        .map(({ startTime }) => startTime),
    [timeSlots, amenity, locationId],
  )

  const anchor =
    isRangeAmenity &&
    value &&
    typeof value.startTime === 'number' &&
    typeof value.endTime !== 'number'
      ? value.startTime
      : null

  const availableStartTimes = useMemo(
    () => new Set(timeSlots.map((slot) => slot.startTime)),
    [timeSlots],
  )

  const disabledSlots = useMemo(() => {
    if (anchor === null) return capacityDisabledSlots

    const relativeDisabled = timeSlots
      .map((slot) => slot.startTime)
      .filter((time) => {
        if (time === anchor) return false
        const duration = Math.abs(time - anchor) + timeInterval
        if (duration < minTime || duration > maxTime) return true
        const lo = Math.min(anchor, time)
        const hi = Math.max(anchor, time)
        for (let slot = lo; slot <= hi; slot += timeInterval) {
          if (!availableStartTimes.has(slot)) return true
        }
        return isDisabledAmongRange(lo, hi, capacityDisabledSlots)
      })

    return Array.from(new Set([...capacityDisabledSlots, ...relativeDisabled]))
  }, [
    anchor,
    capacityDisabledSlots,
    timeSlots,
    minTime,
    maxTime,
    timeInterval,
    availableStartTimes,
  ])

  const onClickTimeSlot = (clicked) => {
    if (disabledSlots.includes(clicked)) return

    if (!isRangeAmenity) {
      onValueChange({ startTime: clicked })
      return
    }

    if (anchor === null) {
      onValueChange({ startTime: clicked })
      return
    }

    if (clicked === anchor) {
      onValueChange(undefined)
      return
    }

    onValueChange({
      startTime: Math.min(anchor, clicked),
      endTime: Math.max(anchor, clicked),
    })
  }

  return (
    <div className="reservation-time-selection">
      <div className="reservation-time-selection__calendar">
        <div className="reservation-time-selection__header">
          <button
            className="reservation-time-selection__header-back-button "
            onClick={onGoBack}
            type="button"
          >
            {'<'}
          </button>
          <span className="reservation-time-selection__header-title">
            {format(selectedDate, 'dd MMMM yyyy')}
          </span>
          <span />
        </div>
        {loadingTimeScopes ? (
          <div className="reservation-time-selection__loader-container">
            <Loader />
          </div>
        ) : (
          <div className="reservation-time-selection__timeslots">
            {(!timeSlots || !timeSlots.length) && !loadingTimeScopes && (
              <div className="reservation-time-selection__no-timeslots">
                {t('reservations:reservationTimeSelect.noAvailableSlots')}
              </div>
            )}
            {!!timeSlots &&
              !!timeSlots.length &&
              timeSlots.map((timeSlot) => {
                const time = timeSlot.startTime
                const isRangeSelected =
                  value && typeof value.startTime === 'number' && typeof value.endTime === 'number'
                const selected =
                  value &&
                  (value.startTime === time ||
                    value.endTime === time ||
                    (isRangeSelected && time > value.startTime && time < value.endTime))
                const bookedDisabled = capacityDisabledSlots.includes(time)
                const limited = !bookedDisabled && disabledSlots.includes(time)

                const [slot] = getHourlySlotsForTime(
                  amenity,
                  new Date(selectedDate).getDay(),
                  time,
                  time + amenity.timeInterval,
                )

                return (
                  <div
                    key={timeSlot.startTime}
                    onClick={() => onClickTimeSlot(time)}
                    className={`reservation-time-selection__timeslot ${
                      bookedDisabled ? 'reservation-time-selection__timeslot-disabled' : ''
                    } ${limited ? 'reservation-time-selection__timeslot-limited' : ''} ${
                      selected ? 'reservation-time-selection__timeslot-selected' : ''
                    }`}
                  >
                    <div className="reservation-time-selection__timeslot-time">
                      {dayMinutesToHoursMinutes(time)}
                    </div>
                    <div className="reservation-time-selection__timeslot-price">
                      {formatPrice(slot?.price * 100)}
                    </div>
                  </div>
                )
              })}
          </div>
        )}
      </div>
    </div>
  )
}

ReservationTimeSelect.propTypes = {
  amenity: PropTypes.object,
  selectedDate: PropTypes.object,
  onValueChange: PropTypes.func,
  onGoBack: PropTypes.func,
  value: PropTypes.object,
  timeSlots: PropTypes.array,
  loadingTimeScopes: PropTypes.bool,
  locationId: PropTypes.number,
}

export default ReservationTimeSelect
